export const NAV = [
    { label: "Home", path: "/" },
    { label: "How It Works", path: "/how-it-works" },
    { label: "Solutions", path: "/solutions" },
    { label: "Business X-Ray", path: "/business-x-ray" },
    { label: "Who We Help", path: "/who-we-help" },
    { label: "About", path: "/about" },
    { label: "Insights", path: "/insights" },
    { label: "Contact", path: "/contact" },
];

export const SEO = {
    "/": {
        title: "Vantera | AI-Powered Revenue Growth & Cost Reduction for Established B2B Companies",
        description:
            "Vantera helps established B2B companies grow profitable revenue, recover lost opportunities, improve sales and marketing performance, and reduce unnecessary operating costs.",
    },
    "/how-it-works": {
        title: "How It Works | Vantera",
        description:
            "Diagnose, establish the baseline, prioritize, deploy, operate, measure, scale. Vantera works around your existing people, systems, customers, vendors, and data.",
    },
    "/solutions": {
        title: "Solutions | Revenue Growth, Recovery & Cost Reduction | Vantera",
        description:
            "Increase revenue. Recover what you already earned. Reduce what you shouldn't be spending. Vantera attacks profitable growth from both sides of the P&L.",
    },
    "/business-x-ray": {
        title: "Business X-Ray | Find Hidden Revenue, Savings & Margin Opportunities | Vantera",
        description:
            "The Vantera Business X-Ray is a diagnostic examination of your revenue engine and operating economics — a prioritized Profit Opportunity Map, not a generic consulting report.",
    },
    "/who-we-help": {
        title: "Who We Help | Established B2B Companies | Vantera",
        description:
            "Built for established B2B companies — approx. $30M–$150M revenue — with sales infrastructure, CRM data, meaningful transaction values, and capacity to fulfill additional demand.",
    },
    "/about": {
        title: "About Vantera | Build Smarter, More Profitable Revenue Organizations",
        description:
            "Revenue growth should not require cost to grow at the same rate. Vantera makes the growth assets inside established companies work together more intelligently.",
    },
    "/insights": {
        title: "Insights | Vantera",
        description:
            "Executive analysis on revenue growth, revenue recovery, sales productivity, AI & automation, marketing economics, margin improvement, and cost reduction.",
    },
    "/contact": {
        title: "Request an Executive Review | Vantera",
        description:
            "Find out where your next profit opportunity is hiding. Request an Executive Review with Vantera — the first step toward the Business X-Ray.",
    },
};

export const LEVERS = [
    {
        id: "grow",
        num: "01",
        name: "GROW",
        sub: "New Revenue",
        tone: "vantera",
        blurb:
            "Identify high-value accounts, generate qualified demand, improve conversion, and increase sales productivity.",
        caps: ["High-value account intelligence", "Qualified demand generation", "Better targeting", "Sales conversion", "Sales productivity"],
    },
    {
        id: "recover",
        num: "02",
        name: "RECOVER",
        sub: "Existing Revenue",
        tone: "azure",
        blurb:
            "Reactivate dormant customers, lost opportunities, expired quotes, old leads, and underpenetrated accounts.",
        caps: ["Dormant customers", "Lost opportunities", "Expired quotes", "Old leads", "Lapsed buyers", "Cross-sell", "Upsell", "Renewals"],
    },
    {
        id: "reduce",
        num: "03",
        name: "REDUCE",
        sub: "Operating Costs",
        tone: "ink",
        blurb:
            "Audit operating expenses during the Business X-Ray and identify categories where the company may be overpaying.",
        caps: ["Vendor costs", "Recurring operating expenses", "Margin leakage", "Pricing inconsistencies", "Administrative inefficiencies", "Cost-to-serve"],
    },
];

export const LEAKS = [
    "Slow lead response",
    "Inconsistent follow-up",
    "Weak targeting",
    "Stale CRM data",
    "Lost opportunities",
    "Expired quotes",
    "Dormant customers",
    "Advertising inefficiency",
    "Sales bottlenecks",
    "Margin leakage",
    "Overpriced vendors",
    "Unnecessary recurring expenses",
];

export const BENTO = [
    { tag: "REVENUE LEAKAGE", span: "md:col-span-2", desc: "Revenue escaping through slow response, weak follow-up, and unworked demand." },
    { tag: "DORMANT CUSTOMERS", span: "", desc: "Past buyers who already trust you — and have not been re-engaged." },
    { tag: "ADVERTISING WASTE", span: "", desc: "Spend that never reaches qualified pipeline, attributed and audited." },
    { tag: "SALES BOTTLENECKS", span: "", desc: "Stage aging, slow quotes, and rep hours lost to research and admin." },
    { tag: "MARGIN LEAKAGE", span: "md:col-span-2", desc: "Pricing variance, discounting drift, low-margin accounts, and cost-to-serve gaps." },
    { tag: "VENDOR OVERPAYMENT", span: "", desc: "Recurring expenses priced above what the market may offer." },
    { tag: "CONVERSION OPPORTUNITIES", span: "", desc: "Qualified demand that exists today but dies inside the funnel." },
    { tag: "AUTOMATION OPPORTUNITIES", span: "", desc: "Repetitive work AI can absorb while humans keep judgment." },
];

export const XRAY_CATEGORIES = [
    { num: "01", name: "Executive Economic Baseline", desc: "Revenue, gross profit, growth, sales organization, marketing investment, transaction value, sales cycle, fulfillment capacity, and key operating economics." },
    { num: "02", name: "Funnel & Sales Economics", desc: "Conversion, response time, follow-up, stage aging, quoting, lost deals, pipeline quality, and sales productivity." },
    { num: "03", name: "Customer & Market Economics", desc: "Revenue and profitability by customer, segment, geography, product/service, cohort, ICP characteristics, and target-market opportunity." },
    { num: "04", name: "Marketing & Advertising Efficiency", desc: "Where marketing dollars are producing pipeline, revenue, and gross profit — and where they are not." },
    { num: "05", name: "Dormant & Recoverable Revenue", desc: "Lost deals, expired quotes, old leads, dormant customers, lapsed buyers, cross-sell/upsell opportunities, and underworked accounts." },
    { num: "06", name: "Margin Leakage", desc: "Pricing variance, discounting, low-margin business, cost-to-serve, and administrative inefficiency." },
    { num: "07", name: "Cost Reduction & Vendor Audit", desc: "We review eligible recurring expenses and vendor categories to identify potential overpayment or better commercial terms — pursued through our cost-reduction partners and network of 150+ vendors where appropriate." },
    { num: "08", name: "AI & Automation Opportunities", desc: "Identify repetitive work where AI and automation can create leverage while maintaining appropriate human oversight." },
    { num: "09", name: "90-Day Economic Opportunity Roadmap", desc: "Prioritize each opportunity by potential economic impact, speed, confidence, implementation effort, ownership, KPI, and launch priority." },
];

export const STEPS = [
    {
        num: "01",
        name: "Business X-Ray",
        title: "Diagnose",
        desc: "We examine the complete economics of the business — revenue and gross profit, funnel conversion and sales productivity, customer economics and CRM utilization, marketing and advertising efficiency, dormant and recoverable revenue, pricing and margin leakage, vendor costs and recurring operating expenses, and AI and automation opportunities.",
        viz: "scan",
    },
    {
        num: "02",
        name: "Establish the Baseline",
        title: "Baseline",
        desc: "We establish current pipeline, revenue by source, conversion, marketing spend, acquisition economics, sales velocity, gross profit, dormant opportunity pools, and relevant operating-cost categories. This creates a measurable starting point.",
        viz: "baseline",
    },
    {
        num: "03",
        name: "Prioritize the Highest-Value Opportunities",
        title: "Prioritize",
        desc: "Every initiative is ranked using Economic Impact × Speed × Confidence × Effort. This prevents the business from wasting resources on disconnected tactics.",
        viz: "matrix",
    },
    {
        num: "04",
        name: "Deploy the Revenue & Efficiency System",
        title: "Deploy",
        desc: "High-value account intelligence and prospecting. Inbound qualification and AI-assisted follow-up. Revenue recovery and CRM reactivation. Advertising optimization. Sales enablement and workflow automation. Cost-reduction partner review for eligible expense categories. Executive reporting and KPI visibility.",
        viz: "deploy",
    },
    {
        num: "05",
        name: "Operate, Measure & Optimize",
        title: "Operate",
        desc: "Vantera helps operate the workflows rather than simply handing over software. Performance is measured against pipeline, revenue, gross profit, efficiency, recovered revenue, and verified savings — then optimized and scaled.",
        viz: "measure",
    },
];

export const PHASES = [
    {
        days: "DAYS 1–30",
        name: "Diagnose",
        items: ["Economic baseline", "CRM/data audit", "Revenue opportunity audit", "Cost review", "Prioritization"],
    },
    {
        days: "DAYS 31–60",
        name: "Deploy",
        items: ["Revenue workflows", "Recovery programs", "Marketing optimization", "Sales enablement", "Cost-reduction initiatives"],
    },
    {
        days: "DAYS 61–90",
        name: "Optimize",
        items: ["Improve conversion", "Scale winning initiatives", "Quantify economic impact", "Document playbooks", "Establish operating cadence"],
    },
];

export const FAQS = [
    {
        q: "Is Vantera a marketing agency?",
        a: "No. Marketing can be part of the system, but Vantera operates across acquisition, sales execution, follow-up, revenue recovery, advertising intelligence, CRM workflows, sales productivity, analytics, margin, and cost reduction.",
    },
    {
        q: "Is Vantera software?",
        a: "No. Vantera uses AI, automation, data, and technology, but the model is not based on handing your team another software login. We help build and operate the system.",
    },
    {
        q: "Does Vantera replace our sales team?",
        a: "No. The objective is to make the existing sales organization more productive while automating or accelerating appropriate repetitive work.",
    },
    {
        q: "What is the Business X-Ray?",
        a: "A diagnostic review of the company's revenue engine and operating economics designed to identify revenue growth, revenue recovery, margin improvement, automation, and cost-reduction opportunities.",
    },
    {
        q: "How does the cost-reduction component work?",
        a: "During the Business X-Ray, Vantera reviews eligible expense and vendor categories for potential overpayment or inefficiency. Where opportunities are identified, our cost-reduction partners and network of 150+ vendors can help pursue improved pricing, lower costs, or more efficient alternatives.",
    },
    {
        q: "Do we need to replace our CRM or existing vendors?",
        a: "Not necessarily. Vantera is designed to work around established systems. Changes should be made only where the economics support them.",
    },
    {
        q: "What does Vantera measure?",
        a: "Depending on the engagement: qualified pipeline, conversion, revenue, gross profit contribution, sales velocity, acquisition costs, advertising efficiency, recovered revenue, sales productivity, identified savings, realized savings, and margin improvement.",
    },
];

export const SOLUTIONS = [
    {
        num: "01",
        name: "High-Value Account Intelligence",
        desc: "Identify and prioritize the accounts most likely to create meaningful economic value using ICP modeling, account scoring, buying signals, triggers, stakeholder mapping, and historical performance.",
        viz: "accounts",
    },
    {
        num: "02",
        name: "Qualified Demand Generation",
        desc: "Coordinate paid search, LinkedIn, account-based campaigns, retargeting, executive outbound, content/search, and channel opportunities around qualified pipeline and revenue — not vanity metrics.",
        viz: "demand",
    },
    {
        num: "03",
        name: "AI-Powered Follow-Up & Sales Productivity",
        desc: "Improve response speed, qualification, routing, scheduling, follow-up, CRM hygiene, account research, stage-aging alerts, and next-best actions so salespeople spend more time selling.",
        viz: "followup",
    },
    {
        num: "04",
        name: "Revenue Recovery",
        desc: "Identify and reactivate dormant customers, lost opportunities, expired quotes, unworked leads, lapsed buyers, cross-sell/upsell opportunities, renewals, and underpenetrated accounts.",
        viz: "recovery",
    },
    {
        num: "05",
        name: "Advertising Intelligence",
        desc: "Connect Advertising Spend → Lead → Qualified Opportunity → Sale → Gross Profit so leadership can see which channels create economic return and where spend should be increased or reduced.",
        viz: "funnel",
    },
    {
        num: "06",
        name: "Margin & Cost Reduction",
        desc: "Vantera examines pricing inconsistency, discounting, low-margin accounts, cost-to-serve issues, administrative inefficiency, and vendor spend — pursuing savings through cost-reduction partners and a network of 150+ vendors where appropriate. Every dollar saved can improve profitability without requiring another dollar of revenue.",
        viz: "waterfall",
    },
    {
        num: "07",
        name: "AI Revenue & Executive Intelligence",
        desc: "Deploy specialized AI-supported workflows for research, prospecting, inbound response, follow-up, recovery, advertising analysis, margin analysis, and executive reporting. Human approval remains in place for brand, pricing, negotiation, sensitive communications, and high-impact decisions.",
        viz: "exec",
    },
];

export const INDUSTRIES = [
    { name: "Industrial Equipment Manufacturers & Distributors", img: "https://images.unsplash.com/photo-1593106410288-caf65eca7c9d?auto=format&fit=crop&w=1200&q=80", alt: "Laser cutting machine slicing through metal sheet in an industrial manufacturing facility" },
    { name: "Industrial Services", img: "https://images.unsplash.com/photo-1742163960842-bc0e7ce24ac2?auto=format&fit=crop&w=1200&q=80", alt: "Industrial control gauges and instrumentation panels" },
    { name: "Commercial HVAC & Specialty Contractors", img: "https://images.unsplash.com/photo-1698479603408-1a66a6d9e80f?auto=format&fit=crop&w=1200&q=80", alt: "Commercial rooftop HVAC units on an industrial building" },
    { name: "Building Automation & Security", img: "https://images.unsplash.com/photo-1653108951561-b9198632de97?auto=format&fit=crop&w=1200&q=80", alt: "Building systems control panel with status indicator lights" },
    { name: "Specialty B2B Construction", img: "https://images.unsplash.com/uploads/14123892966835548e7bd/14369636?auto=format&fit=crop&w=1200&q=80", alt: "Steel roof structure of a commercial building against bright sky" },
];

export const PROFILE = [
    "Approximately $30M–$150M in annual revenue (guideline, not a rigid requirement)",
    "Established B2B business model",
    "Existing sales organization and CRM/customer data",
    "Meaningful transaction values",
    "Active marketing investment",
    "Recurring operating/vendor expenses with potential savings opportunities",
    "Growth-oriented leadership",
    "Capacity to fulfill additional demand",
];

export const EXECUTIVES = ["CEOs", "Presidents", "CROs", "COOs", "Revenue leaders", "Sales leaders", "Marketing leaders", "CFO / finance leadership", "Private-equity operating partners"];

export const OUTCOMES = ["Qualified pipeline and attributable revenue", "Conversion and sales-cycle velocity", "Recovered revenue", "Advertising efficiency", "Sales productivity", "Gross profit contribution", "Identified and realized cost savings", "Margin improvement"];

export const ASSETS = ["Customers", "CRM data", "Salespeople", "Advertising history", "Past opportunities", "Vendor relationships", "Operating data", "Market knowledge"];

export const INSIGHT_CATEGORIES = ["Revenue Growth", "Revenue Recovery", "Sales Productivity", "AI & Automation", "Marketing Economics", "Margin Improvement", "Cost Reduction", "Executive Intelligence"];

export const IMAGES = {
    about: "https://images.unsplash.com/photo-1512403754473-27835f7b9984?auto=format&fit=crop&w=1600&q=80",
    whoWeHelp: "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1600&q=80",
    xrayTeaser: "https://images.unsplash.com/photo-1524514587686-e2909d726e9b?auto=format&fit=crop&w=1600&q=80",
};